import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, RotateCcw, Check, Info } from 'lucide-react';
import { platformLabel } from '@/lib/clipPlatformFormats';

// Hand-written template pools — the SAME honest approach already used by
// `PublishingMetadataDemo.jsx` ("Sample AI-Generated Metadata (Demo —
// local data only)"). No LLM call, no new provider, no usage charge.
// This is intentionally NOT presented as a real AI service — see the
// "Simulated" badge below and the completion report.
const DESCRIPTION_TEMPLATES = [
  (t, cta) => `New content is live!${t ? ` Today: ${t}.` : ''}${cta ? ` ${cta}` : ' Like, comment, and follow for more.'}`,
  (t, cta) => `${t ? `${t} — ` : ''}here's everything you need to know.${cta ? ` ${cta}` : ' Save this and share it with a friend!'}`,
  (t, cta) => `Another one for the archive.${t ? ` This time: ${t}.` : ''}${cta ? ` ${cta}` : ' Let me know your thoughts below.'}`,
];
const CTA_TEMPLATES = ['Follow for more like this', 'Comment your thoughts below', 'Share this with someone who needs it', 'Link in bio for the full version'];
const HASHTAG_POOLS = {
  youtube: ['#youtube', '#tutorial', '#howto', '#contentcreator'],
  tiktok: ['#fyp', '#viral', '#foryoupage', '#trending'],
  instagram: ['#reels', '#explorepage', '#instagood', '#contentcreator'],
  facebook: ['#facebook', '#community', '#creator'],
};

function buildSuggestion({ contentTitle, platform, brandKit, variantIndex }) {
  const idx = variantIndex % DESCRIPTION_TEMPLATES.length;
  const cta = CTA_TEMPLATES[variantIndex % CTA_TEMPLATES.length];
  const brandSuffix = brandKit?.tagline ? ` ${brandKit.tagline}` : '';
  return {
    title: `${contentTitle || 'Untitled'}${brandKit?.name ? ` | ${brandKit.name}` : ''}`,
    description: DESCRIPTION_TEMPLATES[idx](contentTitle, cta) + brandSuffix,
    cta,
    hashtags: (HASHTAG_POOLS[platform] || HASHTAG_POOLS.youtube).join(' '),
    contentKeywords: [contentTitle, platform, brandKit?.name].filter(Boolean).map((s) => String(s).toLowerCase()),
  };
}

/**
 * Optimize → AI Tools: strictly user-triggered (section 8 — never runs on
 * load, selection, or platform switch). Shows a proposal the creator must
 * explicitly apply field-by-field or all at once; nothing is overwritten
 * silently, and protected fields (anything already non-empty) are called
 * out before applying.
 */
export function OptimizeAIPanel({ variant, contentTitle, brandKit, onApplySuggestion, disabled }) {
  const [suggestion, setSuggestion] = useState(null);
  const [variantIndex, setVariantIndex] = useState(0);

  const generate = () => {
    const next = buildSuggestion({ contentTitle, platform: variant.platforms[0], brandKit, variantIndex });
    setSuggestion(next);
  };
  const regenerate = () => {
    const nextIdx = variantIndex + 1;
    setVariantIndex(nextIdx);
    setSuggestion(buildSuggestion({ contentTitle, platform: variant.platforms[0], brandKit, variantIndex: nextIdx }));
  };

  if (disabled) return <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="optimize-ai-empty">Select content and a platform first.</p>;

  return (
    <div className="space-y-3" data-testid="optimize-ai-panel">
      <div className="flex items-center justify-between">
        <Badge variant="outline" className="border-fuchsia-500/30 text-fuchsia-300 text-[10px]" data-testid="optimize-ai-simulated-badge">Simulated Suggestions — not a real AI call</Badge>
      </div>
      <p className="text-[11px] text-[#8A93A6] flex items-start gap-1.5">
        <Info className="h-3 w-3 mt-0.5 shrink-0" />
        Built locally from this content's title, {platformLabel(variant.platforms[0])}, and your Brand Kit — nothing is sent to an AI provider and nothing applies until you choose to.
      </p>

      <Button size="sm" className="w-full bg-fuchsia-600 hover:bg-fuchsia-700" onClick={generate} data-testid="optimize-ai-generate-button">
        <Sparkles className="h-3.5 w-3.5 mr-1.5" />Generate AI Suggestions
      </Button>

      {suggestion && (
        <div className="p-3 rounded-lg border border-white/10 bg-white/[0.02] space-y-2.5" data-testid="optimize-ai-proposal">
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-[#8A93A6]">Proposed changes — review before applying</span>
            <button onClick={regenerate} className="text-[10px] text-[#8A93A6] hover:text-white flex items-center gap-1" data-testid="optimize-ai-regenerate-button"><RotateCcw className="h-2.5 w-2.5" />Regenerate</button>
          </div>
          {[
            { field: 'title', label: 'Title', value: suggestion.title },
            { field: 'description', label: 'Description', value: suggestion.description },
            { field: 'cta', label: 'CTA', value: suggestion.cta },
            { field: 'hashtags', label: 'Hashtags', value: suggestion.hashtags },
          ].map((row) => {
            const currentValue = variant[row.field];
            const wouldOverwrite = !!currentValue?.trim?.();
            return (
              <div key={row.field} className="text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-[10px] text-[#555E6E]">{row.label}{wouldOverwrite && <span className="text-amber-400"> · you already have content here</span>}</span>
                  <button onClick={() => onApplySuggestion(row.field, row.value)} className="text-[10px] text-[#3A7BFF] hover:underline flex items-center gap-1" data-testid={`optimize-ai-apply-${row.field}`}><Check className="h-2.5 w-2.5" />Apply</button>
                </div>
                <p className="text-[#C0C7D4] mt-0.5 line-clamp-2">{row.value}</p>
              </div>
            );
          })}
          <div className="text-xs">
            <div className="flex items-center justify-between">
              <span className="text-[10px] text-[#555E6E]">Content keywords</span>
              <button onClick={() => onApplySuggestion('contentKeywords', suggestion.contentKeywords)} className="text-[10px] text-[#3A7BFF] hover:underline flex items-center gap-1" data-testid="optimize-ai-apply-keywords"><Check className="h-2.5 w-2.5" />Add</button>
            </div>
            <p className="text-[#C0C7D4] mt-0.5">{suggestion.contentKeywords.join(', ')}</p>
          </div>
        </div>
      )}
    </div>
  );
}
